import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';
import CloseIcon from '@material-ui/icons/Close';

import cloneDeep from 'lodash/cloneDeep';
import flowRight from 'lodash/flowRight';
import get from 'lodash/get';

import * as CompanyFeatures from '../../services/CompanyFeatures';
import * as SharedOptions from '../../services/SharedOptions';
import * as Authorization from '../../utils/AuthorizationChecks';
import * as Functions from '../../utils/Functions';
import * as FieldValidation from '../crud/CreateAndEditResource/FieldValidation';
import SearchableSelect from './SearchableSelect';
import { resourceFields } from '../../data/Constants';
import { createEditOptionDialogStyles } from '../../css/style';
import {
  bool,
  func,
  arrayOf,
  object,
  objectOf,
  string,
  number,
} from 'prop-types';
import { buildSearchableEntities } from '../../domains/Restrictions';

const { getFieldIsInvalidStateKey, getInvalidFieldMessageStateKey } = FieldValidation;

const DialogHeader = ({ classes, createOrEdit, onClose }) => (
  <section className={classes.dialogHeaderParent}>
    <section className={classes.dialogTitleSection}>{`${createOrEdit} Modifier Option`}</section>
    <section className={classes.closeIconSection}>
      <IconButton className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </section>
  </section>
);

export const DIALOG_TYPES = Object.freeze({
  CREATE: 'Create',
  EDIT: 'Edit',
});

class BaseCreateEditOptionDialog extends Component {
  constructor(props) {
    super(props);

    const state = this.initializeFields(props);
    this.state = state;
  }

  /**
   * This initializes the form state when you open, or close and reopen, the Dialog.
   *
   * MUI Dialogs start off always "rendered", as in they will "exist", sense prop changes,
   * and run their render functions when props change. Whether they actually show up
   * in the DOM depends on their props.open.
   */
  componentDidUpdate(prevProps) {
    const { open: wasOpen } = prevProps;
    const { open: isOpen } = this.props;
    if (!wasOpen && isOpen) {
      const state = this.initializeFields(this.props);
      this.setState(state);
    }
  }

  initializeFields = (props) => {
    const {
      createOrEdit,
      admin,
      match,
      customizations,
      options,
      optionId,
    } = props;

    const authorizedFields = this.getAuthorizedFields(admin);

    let state = {
      disableSaveButton: false,
      fieldKeysForIteration: [],
      resourceFieldsAsMap: {},
      originalCustomizationsMap: {},
      whichOutOfStockOptions: 'withSelectAll',
    };

    const fakeResources = { customizations, options };

    const initializeArgs = {
      fields: authorizedFields,
      state,
      match,
      resources: fakeResources,
      id: optionId,
    };

    if (createOrEdit === 'Create') state = this.initializeFieldsForCreate(initializeArgs);
    if (createOrEdit === 'Edit') state = this.initializeFieldsForEdit(initializeArgs);
    return state;
  }

  // If unsure, treat as location admin and disable editing most fields
  getAuthorizedFields(admin) {
    return !admin || Authorization.checkIsLocationAdmin(admin)
      ? resourceFields.optionsForLocationAdmins
      : resourceFields.options;
  }

  initializeFieldsForCreate({ fields, state, match }) {
    fields.forEach((field) => {
      const { key, type: fieldType } = field;
      state.resourceFieldsAsMap[key] = field;
      state[getFieldIsInvalidStateKey(key)] = false;
      state[getInvalidFieldMessageStateKey(key)] = '';
      state.fieldKeysForIteration.push(key);
      if (!fieldType || fieldType === 'number' || fieldType === 'decimal') return state[key] = '';
      if (fieldType === 'dropdown') return state[key] = [];
    });
    state.originalOOSLocations = [];
    return state;
  }

  getCurrentOption = ({
    id,
    match,
    resources,
  }) => {
    const { isFromEditModifier, optionListFromCreateEditModifier } = this.props;
    if (isFromEditModifier) {
      const currentOption = optionListFromCreateEditModifier.find(option => option.id === Number(id));
      return cloneDeep(currentOption);
    }
    const findFunc = match.subResource === 'options' ? Functions.findSubEntityByID : Functions.findEntityByID;
    const findArgs = { resources, match, id };
    const currentOption = findFunc(findArgs);
    return cloneDeep(currentOption);
  }

  initializeFieldsForEdit = ({
    fields,
    id,
    match,
    resources,
    state,
  }) => {
    const currentOption = this.getCurrentOption({ id, match, resources });
    if (!currentOption) return state;

    fields.forEach((field) => {
      const { key, type: fieldType } = field;
      state.resourceFieldsAsMap[key] = field;
      state.fieldKeysForIteration.push(key);
      const lookupKey = key === 'outOfStock' ? 'outOfStockLocations' : field.key;
      const value = currentOption[lookupKey];

      if (!fieldType || fieldType === 'number' || fieldType === 'decimal') state[key] = value;
      if (['outOfStock', 'customizations'].includes(key)) state[key] = buildSearchableEntities(value);
      if (key === 'customizations') state.originalCustomizationsMap = Functions.getIdsMap(value);

      const isInvalid = this.isFieldInvalid(field, value);
      state[getFieldIsInvalidStateKey(key)] = isInvalid;
      state[getInvalidFieldMessageStateKey(key)] = FieldValidation.getErrorMessage(field, isInvalid);
    });
    return state;
  }

  dialogPaperClass = { paper: this.props.classes.dialogPaper }

  textFieldFormControlClasses = { root: this.props.classes.textFieldFormControl }

  errorBorderClasses = { root: this.props.classes.errorBorder }

  inputClasses = { root: this.props.classes.textFieldMuiInput }

  textFieldInputProps = { disableUnderline: true, classes: this.inputClasses }

  validationErrorClasses = { root: this.props.classes.validationError }

  submitGridContainerClasses = { container: this.props.classes.submitGridContainer }

  saveButtonClasses = { root: this.props.classes.submitButton, disabled: this.props.classes.disabledSubmitButton }

  renderTextField = (field) => {
    const { admin } = this.props;
    const treatAsLocationAdmin = !admin || Authorization.checkIsLocationAdmin(admin);

    const fieldKey = field.key;
    const value = this.state[fieldKey];

    const hasError = this.state[getFieldIsInvalidStateKey(fieldKey)];
    const isInvalidMessage = this.state[getInvalidFieldMessageStateKey(fieldKey)];
    return (
      <Grid item xs={6}>
        <Typography>{field.title}</Typography>
          <TextField
            disabled={treatAsLocationAdmin}
            classes={hasError ? this.errorBorderClasses : this.textFieldFormControlClasses}
            InputProps={this.textFieldInputProps}
            onChange={this.onChangeTextField(fieldKey)}
            value={value}
          />
        <Typography classes={this.validationErrorClasses}>{isInvalidMessage}</Typography>
      </Grid>
    );
  }

  disableCurrentModifierInSearchable = (labelValueObj) => {
    const { resourceId } = this.props.match.params;
    return labelValueObj.value === Number(resourceId);
  }

  renderModifierSearchable = (field) => {
    const { admin, match } = this.props;
    const hasSharedOptions = CompanyFeatures.isSharedOptionsEnabled(admin);
    if (!hasSharedOptions) return null;
    const treatAsLocationAdmin = !admin || Authorization.checkIsLocationAdmin(admin);
    const { searchableCustomizations } = this.props;
    const { customizations } = this.state;

    return (
      <Grid item xs={12}>
        <Typography>{field.title}</Typography>
        <SearchableSelect
          admin={admin}
          backspaceRemovesValue={false}
          fieldKey={field.key}
          isDisabled={!Array.isArray(searchableCustomizations) || treatAsLocationAdmin}
          isMulti
          isOptionDisabled={this.disableCurrentModifierInSearchable}
          match={match}
          maxMenuHeight={350}
          options={searchableCustomizations}
          onChange={this.onChangeModifiers}
          value={customizations}
        />
      </Grid>
    );
  }

  renderFields = () => {
    const { admin } = this.props;
    const authorizedFields = this.getAuthorizedFields(admin);
    return authorizedFields.map((field) => {
      if (field.key === 'outOfStock') return this.renderOOSDropdown(field);
      if (field.key === 'customizations') return this.renderModifierSearchable(field);
      return this.renderTextField(field);
    });
  }

  renderOOSDropdown = (field) => {
    const { searchableLocations, searchableLocationsWithSelectAll } = this.props;

    const { whichOutOfStockOptions } = this.state;
    const finalOptions = whichOutOfStockOptions === 'withSelectAll'
      ? searchableLocationsWithSelectAll
      : searchableLocations;

    const value = this.state.outOfStock;

    return (
      <Grid item xs={12}>
        <Typography>{field.title}</Typography>
        <SearchableSelect
          isDisabled={!Array.isArray(searchableLocations)}
          isMulti
          maxMenuHeight={200}
          options={finalOptions}
          onChange={this.onChangeOOSLocations}
          value={value}
        />
      </Grid>
    );
  }


  validateFieldsBeforeSubmit = () => {
    const { fieldKeysForIteration } = this.state;
    const newIsFieldInvalidMap = {};
    const fieldErrorMessageMap = {};
    let disableSaveButton = false;
    fieldKeysForIteration.forEach((fieldKey) => {
      const value = this.state[fieldKey];
      const field = this.state.resourceFieldsAsMap[fieldKey];
      const isInvalid = this.isFieldInvalid(field, value);
      if (isInvalid) disableSaveButton = true;
      newIsFieldInvalidMap[getFieldIsInvalidStateKey(fieldKey)] = isInvalid;
      const errorMessage = FieldValidation.getErrorMessage(field, isInvalid);
      fieldErrorMessageMap[getInvalidFieldMessageStateKey(fieldKey)] = errorMessage;
    });

    this.setState({ ...newIsFieldInvalidMap, ...fieldErrorMessageMap, disableSaveButton });
    return disableSaveButton;
  }

  prepareOptionForSubmit = () => {
    const { fieldKeysForIteration } = this.state;
    const { isFromEditModifier, createOrEdit } = this.props;
    const option = {};
    fieldKeysForIteration.forEach((fieldKey) => {
      const formValue = this.state[fieldKey];
      const { type: fieldType } = this.state.resourceFieldsAsMap[fieldKey];

      if (['number', 'decimal'].includes(fieldType)) return option[fieldKey] = Number(formValue);
      if (['customizations', 'outOfStock'].includes(fieldKey)) {
        let idNameObjects = Array.isArray(formValue)
          ? formValue.map(searchable => ({ id: searchable.value, name: searchable.label }))
          : [];
        if (fieldKey === 'customizations' && isFromEditModifier && createOrEdit === DIALOG_TYPES.CREATE) {
          const { originalModifierDetails } = this.props;
          const { id, name } = originalModifierDetails;
          idNameObjects.push({ id, name });
        }
        option[fieldKey] = idNameObjects;
        return;
      }
      option[fieldKey] = formValue;
    });
    const { customizations: selectedCustomizations, outOfStock: outOfStockLocations, ...restOption } = option;
    return { selectedCustomizations, outOfStockLocations, restOption };
  }

  getAttachDetachModifiersRequests = (createdOrUpdatedOptionId, selectedCustomizations) => {
    const { originalCustomizationsMap } = this.state;
    const { customizations: allCustomizations, isFromEditModifier, createOrEdit: createOrEditOption } = this.props;

    const selectedCustomizationsIdMap = Functions.getIdsMap(selectedCustomizations);
    const stayedSelectedsMap = {};
    let requests = allCustomizations.map((customization) => {
      const { id } = customization;
      const stayedSelected = originalCustomizationsMap[id] && selectedCustomizationsIdMap[id];
      const isStillNotSelected = !originalCustomizationsMap[id] && !selectedCustomizationsIdMap[id];
      if (stayedSelected) stayedSelectedsMap[id] = { id, name: customization.name };
      const noChange = stayedSelected || isStillNotSelected;
      if (noChange) return null;

      const isNewlySelected = !originalCustomizationsMap[id] && selectedCustomizationsIdMap[id];
      const isNewlyUnselected = originalCustomizationsMap[id] && !selectedCustomizationsIdMap[id];
      if (isNewlySelected) {
        return { actionName: 'addResource', path: ['customizations', id, 'options'] };
      } else if (isNewlyUnselected) {
        return { actionName: 'deleteResource', path: ['customizations', id, 'options', createdOrUpdatedOptionId] };
      }
      return null;
    });

    const emptiesRemoved = requests.filter(request => !!request);
    return { requestsInfo: emptiesRemoved, stayedSelectedsMap };
  }

  submitOption = async () => {
    const isAnyFieldInvalid = this.validateFieldsBeforeSubmit();
    if (isAnyFieldInvalid) return;
    const { selectedCustomizations, outOfStockLocations, restOption } = this.prepareOptionForSubmit();

    const { actions, apiToken, onClose } = this.props;
    const newOption = await this.createOrUpdateOption(restOption);
    if (!newOption) {
      onClose();
      return;
    }
    const successfulOutOfStockResponse = await actions.updateResource(apiToken, outOfStockLocations, 'options', newOption.id, 'out-of-stock');
    const nextOOSLocationsToDisplay = cloneDeep(get(successfulOutOfStockResponse, 'response'));
    const newOptionWithModifiers = await this.submitModifiers(newOption, selectedCustomizations);
    const newOptionForDisplay = Object.assign({}, newOptionWithModifiers, { outOfStockLocations: nextOOSLocationsToDisplay });

    this.updateOptionListInModifierPages(newOptionForDisplay);

    // To refresh customizations in case the user browses a customization they just added this option to
    actions.getAllResources(apiToken, 'customizations');
    onClose();
  }

  onChangeOOSLocations = (value) => {
    if (!value) {
      this.setState({ outOfStock: [], whichOutOfStockOptions: 'withSelectAll' });
      return;
    }
    const lastSelectedOption = value[value.length - 1];
    const { searchableLocations, searchableLocationsWithSelectAll } = this.props;
    const { outOfStock: currentOOSValue } = this.state;

    let finalValue = value;
    let finalOptions = 'withSelectAll';
    if (lastSelectedOption && lastSelectedOption.value === 'all') {
      if (currentOOSValue.length + 1 < searchableLocationsWithSelectAll.length) {
        finalOptions = 'withoutSelectAll';
        finalValue = cloneDeep(searchableLocationsWithSelectAll);
        // Remove 'All Locations' from the selected options tags
        finalValue.shift();
        // + 1 because All Locations is another option
      } else if (currentOOSValue.length + 1 === searchableLocationsWithSelectAll.length) {
        finalValue = [];
      }
    }

    this.setState({ outOfStock: finalValue, whichOutOfStockOptions: finalOptions });
  }

  onChangeTextField = fieldKey => (event) => {
    const value = get(event, 'target.value', '');
    this.setState({ [fieldKey]: value });
    this.validateFieldAndUpdateUI(fieldKey, value);
  }

  isFieldInvalid = (field, val) => {
    const fieldType = field.type;
    const isRequired = field.isRequired;
    if (isRequired && !val) return true;
    if (['number', 'decimal'].includes(fieldType)) {
      const numberValidations = FieldValidation.validateNumberTypes(val, field);
      if (fieldType === 'number') return numberValidations.invalidInt;
      if (fieldType === 'decimal') return numberValidations.invalidDecimal;
    }
    return false;
  }

  getErrorMessage = (field, isInvalid) => {
    if (!isInvalid) return '';
    if (field.isRequired) return `${field.title} is required`;
    if (field.type === 'number') return `${field.title} must be a whole number e.g. 1, 2`;
    if (field.type === 'decimal') return `${field.title} should only have two decimal places`;
  }

  shouldDisableSaveButton = (currentFieldKey, isCurrentFieldInvalid) => {
    if (isCurrentFieldInvalid) return true;
    const { fieldKeysForIteration } = this.state;
    const isAnyFieldInvalid = fieldKeysForIteration.some((fieldKey) => {
      if (fieldKey !== currentFieldKey) return this.state[getFieldIsInvalidStateKey(fieldKey)];
      return isCurrentFieldInvalid;
    });
    return isAnyFieldInvalid;
  }

  validateFieldAndUpdateUI = (fieldKey, val) => {
    const field = this.state.resourceFieldsAsMap[fieldKey];
    const isInvalid = this.isFieldInvalid(field, val);
    const errorMessage = FieldValidation.getErrorMessage(field, isInvalid);
    const disableSaveButton = this.shouldDisableSaveButton(fieldKey, isInvalid);

    this.setState({
      disableSaveButton,
      [getFieldIsInvalidStateKey(fieldKey)]: isInvalid,
      [getInvalidFieldMessageStateKey(fieldKey)]: errorMessage,
    });
  }

  onChangeModifiers = (value) => {
    this.setState({ customizations: value });
  }

  submitModifiers = async (newOption, selectedCustomizations) => {
    const { apiToken, actions } = this.props;
    const { requestsInfo, stayedSelectedsMap } = this.getAttachDetachModifiersRequests(newOption.id, selectedCustomizations);
    const setModifiersArgs = {
      apiToken,
      option: newOption,
      selectedCustomizations,
      requestsInfo,
      stayedSelectedsMap,
    };
    return await actions.setModifiersOnOptions(setModifiersArgs);
  }

  createOrUpdateOption = async (restOption) => {
    const { actions, apiToken } = this.props;
    const { createOrEdit, optionId } = this.props;
    const rightAction = createOrEdit === DIALOG_TYPES.CREATE ? 'addResource' : 'updateResource';
    const resourcePath = createOrEdit === DIALOG_TYPES.CREATE ? ['options'] : ['options', optionId];
    const res = await actions[rightAction](apiToken, restOption, ...resourcePath);
    const newOption = get(res, 'response');
    return newOption;
  }

  /**
   * @param {*} newOptionForDisplay An option with the `customizations` and `outOfStockLocations` keys
   */
  updateOptionListInModifierPages(newOptionForDisplay) {
    const { createOrEdit, isFromEditModifier } = this.props;
    const { onEditOptionForCreateEditModifier, updateParentOptionsList } = this.props;
    const { actions } = this.props;

    // For the Create Option case, actions.addResource will already have added to the store
    // a version without the customizations and OOS Locations, because the API endpoint's
    // Option type does not have those keys. So update that option with those keys now for display.
    actions.updateResourceSuccess(newOptionForDisplay, ['options', newOptionForDisplay.id]);

    const cleanedNewOption = SharedOptions.cleanOptionsKeysForRequests(newOptionForDisplay);
    if (createOrEdit === DIALOG_TYPES.CREATE) {
      if (isFromEditModifier) updateParentOptionsList(newOptionForDisplay);
    } else if (createOrEdit === DIALOG_TYPES.EDIT && typeof onEditOptionForCreateEditModifier === 'function') {
      onEditOptionForCreateEditModifier(newOptionForDisplay);
    }
  }

  render() {
    const {
      classes,
      createOrEdit,
      open,
      onClose,
    } = this.props;

    const { disableSaveButton } = this.state;

    return (
      <Dialog
        open={open}
        classes={this.dialogPaperClass}
        onClose={onClose}
        PaperProps={{ id: '#createEditDialog' }}
      >
        <DialogHeader
          classes={classes}
          createOrEdit={createOrEdit}
          onClose={onClose}
        />
        <Grid container spacing={24}>
          {this.renderFields()}
        </Grid>
        <Grid container justify="center" alignItems='center' classes={this.submitGridContainerClasses}>
          <Button
            disabled={disableSaveButton}
            classes={this.saveButtonClasses}
            onClick={this.submitOption}
          >
            SAVE CHANGES
          </Button>
        </Grid>
      </Dialog>
    );
  }
}

BaseCreateEditOptionDialog.propTypes = {
  actions: objectOf(func),
  admin: object,
  apiToken: string,
  createOrEdit: string,
  customizations: arrayOf(object),
  isFromCreateModifier: bool,
  isFromEditModifier: bool,
  onEditOptionForCreateEditModifier: func,
  onClose: func,
  open: bool,
  optionId: number,
  optionListFromCreateEditModifier: arrayOf(object),
  searchableCustomizations: arrayOf(object),
  searchableLocations: arrayOf(object),
  searchableLocationsWithSelectAll: arrayOf(object),
  updateParentOptionsList: func,
};

const mapStateToProps = state => ({
  customizations: state.getIn(['resources', 'customizations']),
  options: state.getIn(['resources', 'options']),
});

const pipeline = flowRight([withRouter, withStyles(createEditOptionDialogStyles), connect(mapStateToProps)]);
export const CreateEditOptionDialog = pipeline(BaseCreateEditOptionDialog);
